/**
 * @file Signed-out screen: sign in, or create an account (T0.11).
 *
 * A plain email-and-password form for Better Auth, replacing Clerk's hosted `<SignIn>` and
 * `<SignUp>` components. It is PURE: it is handed the two actions as props and reports back a
 * message or nothing, so it renders and tests without a network or a provider. The provider
 * (auth/better.tsx) wires the real client in.
 *
 * "Forgot password?" opens a request form (`RequestResetForm`, also used by the reset page when a
 * link has expired). Its confirmation is IDENTICAL whether or not the address has an account, so
 * the screen cannot be used to find out who is registered.
 *
 * What the form does NOT do is decide anything. The password minimum is stated here only so a
 * short one is caught before the round trip; the provider on the API enforces it, and a rejected
 * request comes back as a message shown in the same place.
 */
import { useState, type FormEvent } from "react";
import { MIN_PASSWORD_LENGTH, PASSWORD_RESET_LINK_MINUTES } from "@neighboros/shared";
import { Button, Card, SecondaryButton, TextField } from "./ui";

type AuthMode = "sign-in" | "sign-up" | "forgot";

const TABS: ReadonlyArray<{ readonly mode: AuthMode; readonly label: string }> = [
  { mode: "sign-in", label: "Sign in" },
  { mode: "sign-up", label: "Create account" },
];

export interface RequestResetFormProps {
  /** Resolves to a message to show (rate limit, transport), or null. Null for ANY address. */
  readonly onRequest: (email: string) => Promise<string | null>;
  /** Optional way back (to sign in). */
  readonly onBack?: () => void;
  readonly initialEmail?: string;
  /** Lead-in shown above the field, e.g. why a new link is needed. */
  readonly intro?: string;
}

/**
 * Ask for a password-reset link. After a successful request it shows ONE confirmation, the same
 * words for every address.
 */
export function RequestResetForm({ onRequest, onBack, initialEmail = "", intro }: RequestResetFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [message, setMessage] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const failure = await onRequest(email.trim());
      if (failure === null) setSent(true);
      else setMessage(failure);
    } catch {
      setMessage("Could not reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="mx-auto w-full max-w-sm">
      <h2 className="text-lg font-semibold tracking-tight">Reset your password</h2>
      {sent ? (
        <div className="mt-4 flex flex-col gap-4">
          <p role="status" className="text-sm">
            If an account exists for that email address, we have sent a link to reset the password.
            The link works once and expires in {PASSWORD_RESET_LINK_MINUTES} minutes. Check your spam folder if it does not arrive.
          </p>
          {onBack === undefined ? null : <SecondaryButton onClick={onBack}>Back to sign in</SecondaryButton>}
        </div>
      ) : (
        <form className="mt-4 flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          <p className="text-sm text-[var(--color-ink-muted)]">
            {intro ?? "Enter your email address and we will send you a link to choose a new password."}
          </p>
          <TextField
            id="reset-email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            required
            maxLength={254}
            autoComplete="email"
          />
          {message === null ? null : (
            <p role="alert" className="text-sm text-destructive">
              {message}
            </p>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? "One moment…" : "Send reset link"}
          </Button>
          {onBack === undefined ? null : <SecondaryButton onClick={onBack}>Back to sign in</SecondaryButton>}
        </form>
      )}
    </Card>
  );
}

export interface AuthFormProps {
  /** Asks for a password-reset link; see RequestResetFormProps.onRequest. */
  readonly onRequestPasswordReset: (email: string) => Promise<string | null>;
  /** Resolves to an error message to show, or null on success (the session then updates). */
  readonly onSignIn: (input: { email: string; password: string }) => Promise<string | null>;
  readonly onSignUp: (input: {
    name: string;
    email: string;
    password: string;
  }) => Promise<string | null>;
}

export function AuthForm({ onSignIn, onSignUp, onRequestPasswordReset }: AuthFormProps) {
  const [mode, setMode] = useState<AuthMode>("sign-in");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSignUp = mode === "sign-up";

  function switchTo(next: AuthMode): void {
    setMode(next);
    // A message about the other form is noise here. The typed email stays; a password does not.
    setMessage(null);
    setPassword("");
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;

    const cleanEmail = email.trim();
    const cleanName = name.trim();
    if (isSignUp && password.length < MIN_PASSWORD_LENGTH) {
      setMessage(`Use at least ${String(MIN_PASSWORD_LENGTH)} characters for your password.`);
      return;
    }

    setSubmitting(true);
    setMessage(null);
    try {
      const failure = isSignUp
        ? await onSignUp({ name: cleanName, email: cleanEmail, password })
        : await onSignIn({ email: cleanEmail, password });
      if (failure !== null) setMessage(failure);
      // On success the provider's session updates and this screen is unmounted by the shell.
    } catch {
      // The provider turns expected failures into messages; this is a transport failure.
      setMessage("Could not reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">NeighborOS</h1>
        <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
          Rent, leases and maintenance for small landlords — without the spreadsheet.
        </p>
      </div>

      {mode === "forgot" ? (
        <RequestResetForm
          onRequest={onRequestPasswordReset}
          initialEmail={email}
          onBack={() => switchTo("sign-in")}
        />
      ) : null}

      {mode === "forgot" ? null : (
      <>
      <div className="flex justify-center gap-2">
        {TABS.map((tab) => {
          const isActive = tab.mode === mode;
          return (
            <button
              key={tab.mode}
              type="button"
              aria-pressed={isActive}
              onClick={() => switchTo(tab.mode)}
              className={
                isActive
                  ? "rounded-lg bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-brand-ink)]"
                  : "rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium text-[var(--color-ink-muted)]"
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="mx-auto w-full max-w-sm">
        <h2 className="text-lg font-semibold tracking-tight">
          {isSignUp ? "Create your account" : "Sign in"}
        </h2>

        <form className="mt-4 flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          {isSignUp ? (
            <TextField
              id="auth-name"
              label="Your name"
              value={name}
              onChange={setName}
              required
              maxLength={120}
              autoComplete="name"
            />
          ) : null}
          <TextField
            id="auth-email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            required
            maxLength={254}
            autoComplete="email"
          />
          <TextField
            id="auth-password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            required
            maxLength={128}
            autoComplete={isSignUp ? "new-password" : "current-password"}
            {...(isSignUp
              ? { hint: `At least ${String(MIN_PASSWORD_LENGTH)} characters.` }
              : {})}
          />

          {message === null ? null : (
            <p role="alert" className="text-sm text-destructive">
              {message}
            </p>
          )}

          <Button type="submit" disabled={submitting}>
            {submitting ? "One moment…" : isSignUp ? "Create account" : "Sign in"}
          </Button>
        </form>

        {isSignUp ? null : (
          <p className="mt-4 text-sm">
            <button
              type="button"
              onClick={() => switchTo("forgot")}
              className="text-[var(--color-brand)] underline underline-offset-2"
            >
              Forgot password?
            </button>
          </p>
        )}
      </Card>
      </>
      )}
    </div>
  );
}
